<h2 mat-dialog-title>{{ 'settings.title' | translate }}</h2>

<mat-dialog-content>

  <!-- Language -->
  <section class="settings-section">
    <p class="description">{{ 'settings.language.description' | translate }}</p>

    <mat-form-field appearance="outline" class="full-width">
      <mat-label>{{ 'settings.language.label' | translate }}</mat-label>
      <mat-select
        [value]="selectedLanguage()"
        (selectionChange)="onLanguageChange($event)"
      >
        @for (language of availableLanguages; track language.code) {
          <mat-option [value]="language.code">
            {{ language.labelKey | translate }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
  </section>

  <mat-divider />

  <!-- Inventory View -->
  <section class="settings-section">
    <p class="description">{{ 'settings.inventory-view.description' | translate }}</p>

    <mat-form-field appearance="outline" class="full-width">
      <mat-label>{{ 'settings.inventory-view.label' | translate }}</mat-label>
      <mat-select
        [value]="selectedInventoryView()"
        (selectionChange)="onInventoryViewChange($event)"
        data-testid="settings-inventory-view-select"
      >
        @for (view of availableInventoryViews; track view.value) {
          <mat-option
            [value]="view.value"
            [attr.data-testid]="'settings-inventory-view-option-' + view.value"
          >
            {{ view.labelKey | translate }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
  </section>

  <mat-divider />

  <!-- Notifications -->
  <section class="settings-section" data-testid="settings-notifications-section">
    <p class="section-title">{{ 'notifications.title' | translate }}</p>
    <p class="description">{{ 'notifications.description' | translate }}</p>

    @if (!notificationSettingsStore.pushSupported()) {
      <p class="hint" data-testid="notifications-unsupported">
        {{ 'notifications.unsupported' | translate }}
      </p>
    } @else {
      <mat-slide-toggle
        [checked]="notificationSettingsStore.remindersEnabled()"
        [disabled]="notificationSettingsStore.isSaving() || notificationSettingsStore.isLoading()"
        (change)="onRemindersEnabledChange($event)"
        data-testid="notifications-enabled-toggle"
      >
        {{ 'notifications.enabled.label' | translate }}
      </mat-slide-toggle>

      <p class="subsection-title">{{ 'notifications.reminder-times.title' | translate }}</p>
      <p class="hint">{{ 'notifications.reminder-times.hint' | translate }}</p>

      @for (time of notificationSettingsStore.reminderTimes(); track $index) {
        <div class="reminder-time-row">
          <mat-form-field appearance="outline" class="reminder-time-field">
            <mat-label>{{ 'notifications.reminder-times.label' | translate }}</mat-label>
            <mat-select
              [value]="time"
              [disabled]="!notificationSettingsStore.remindersEnabled() || notificationSettingsStore.isSaving()"
              (selectionChange)="onReminderTimeChange($index, $event)"
              [attr.data-testid]="'notifications-reminder-time-' + $index"
            >
              @for (option of reminderTimeOptions; track option) {
                <mat-option [value]="option">{{ option }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <button
            mat-icon-button
            type="button"
            [disabled]="notificationSettingsStore.reminderTimes().length <= 1 || !notificationSettingsStore.remindersEnabled() || notificationSettingsStore.isSaving()"
            (click)="onRemoveReminderTime($index)"
            [attr.aria-label]="'notifications.reminder-times.remove' | translate"
            [matTooltip]="'notifications.reminder-times.remove' | translate"
            [attr.data-testid]="'notifications-remove-time-' + $index"
          >
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      }

      <button
        mat-stroked-button
        type="button"
        [disabled]="notificationSettingsStore.reminderTimes().length >= reminderTimesMax || !notificationSettingsStore.remindersEnabled() || notificationSettingsStore.isSaving()"
        (click)="onAddReminderTime()"
        data-testid="notifications-add-time"
      >
        <mat-icon>add</mat-icon>
        {{ 'notifications.reminder-times.add' | translate }}
      </button>

      <p class="subsection-title">{{ 'notifications.channels.title' | translate }}</p>

      <mat-checkbox
        [checked]="notificationSettingsStore.browserChannelEnabled()"
        [disabled]="!notificationSettingsStore.remindersEnabled() || notificationSettingsStore.isSaving()"
        (change)="onBrowserChannelChange($event.checked)"
        data-testid="notifications-browser-channel"
      >
        {{ 'notifications.channels.browser' | translate }}
      </mat-checkbox>

      <div class="channel-row disabled-channel">
        <mat-checkbox disabled data-testid="notifications-telegram-channel">
          {{ 'notifications.channels.telegram' | translate }}
        </mat-checkbox>
        <a
          [href]="telegramIssueUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="coming-soon-link"
          data-testid="notifications-telegram-coming-soon"
        >
          {{ 'notifications.channels.telegram-coming-soon' | translate }}
        </a>
      </div>
    }
  </section>

  <mat-divider />

  <!-- Appearance -->
  <section class="settings-section">
    <p class="section-title">{{ 'settings.theme.title' | translate }}</p>

    <!-- Mode -->
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>{{ 'settings.theme.mode.label' | translate }}</mat-label>
      <mat-select
        [value]="selectedMode()"
        (selectionChange)="onModeChange($event)"
      >
        <!-- Custom trigger: show icon + label in the closed select -->
        <mat-select-trigger>
          @if (currentModeOption(); as m) {
            <div class="select-option">
              <mat-icon>{{ m.icon }}</mat-icon>
              <span>{{ m.labelKey | translate }}</span>
            </div>
          }
        </mat-select-trigger>

        @for (m of availableModes; track m.value) {
          <mat-option [value]="m.value">
            <div class="select-option">
              <mat-icon>{{ m.icon }}</mat-icon>
              <span>{{ m.labelKey | translate }}</span>
            </div>
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <!-- Palette -->
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>{{ 'settings.theme.palette.label' | translate }}</mat-label>
      <mat-select
        [value]="selectedPalette()"
        (selectionChange)="onPaletteChange($event)"
      >
        <!-- Custom trigger: show swatch + label in the closed select -->
        <mat-select-trigger>
          @if (currentPaletteOption(); as p) {
            <div class="select-option">
              <span class="palette-swatch" [style.background]="p.swatch"></span>
              <span>{{ p.labelKey | translate }}</span>
            </div>
          }
        </mat-select-trigger>

        @for (p of availablePalettes; track p.value) {
          <mat-option [value]="p.value">
            <div class="select-option">
              <span class="palette-swatch" [style.background]="p.swatch"></span>
              <span>{{ p.labelKey | translate }}</span>
            </div>
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
  </section>

</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button mat-dialog-close>{{ 'common.close' | translate }}</button>
</mat-dialog-actions>
